<!-- Breadcrumbs -->
<div class="text-sm daisy-breadcrumbs mb-4">
  <ul>
    <li>
      <a
        routerLink="/dashboard/profile/tutor-profile"
        class="text-base-content/50 hover:text-base-content transition-colors"
      >
        <i class="fa-solid fa-id-card mr-1.5"></i>Tutor Profile
      </a>
    </li>
    <li class="text-base-content font-medium">
      <i class="fa-solid fa-pen-to-square mr-1.5"></i>Edit Settings
    </li>
  </ul>
</div>

<!-- Hero Header Banner -->
<div
  class="relative rounded-2xl overflow-hidden mb-8 bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-600 shadow-lg"
>
  <!-- Background pattern overlay -->
  <div class="absolute inset-0 opacity-10">
    <svg
      class="w-full h-full"
      viewBox="0 0 800 200"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="50" cy="50" r="80" fill="white" />
      <circle cx="700" cy="150" r="120" fill="white" />
      <circle cx="400" cy="20" r="60" fill="white" />
    </svg>
  </div>
  <div
    class="relative z-10 px-6 md:px-10 py-8 flex flex-col md:flex-row items-start md:items-center gap-4 justify-between"
  >
    <div class="flex items-center gap-5">
      <div
        class="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0"
      >
        <i class="fa-solid fa-chalkboard-user text-white text-2xl"></i>
      </div>
      <div>
        <h1
          class="text-xl md:text-2xl font-extrabold text-white m-0 leading-tight"
        >
          Tutor Profile Settings
        </h1>
        <p class="text-indigo-200 text-sm mt-1 m-0">
          Configure your teaching credentials, subjects, coverage, pricing and
          more.
        </p>
      </div>
    </div>
    <a
      routerLink="/dashboard/profile/tutor-profile"
      class="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-xl text-sm font-semibold transition-all backdrop-blur-sm border border-white/20 no-underline flex-shrink-0"
    >
      <i class="fa-solid fa-arrow-left"></i>
      Back to Profile
    </a>
  </div>
</div>

<form
  [formGroup]="tutorForm"
  class="min-h-[calc(100vh-80px)] daisy-bg-base-200 py-8 px-4 flex flex-col"
>
  <div class="max-w-4xl mx-auto w-full space-y-8 flex-1">
    <!-- Pending Approval Alert -->
    @if (user?.pendingTutorProfileUpdates) {
      <div
        class="daisy-alert daisy-alert-warning shadow-lg border border-warning/20 rounded-2xl flex flex-col md:flex-row items-start md:items-center gap-4 mb-6"
      >
        <div class="flex items-start gap-4 flex-grow">
          <i
            class="fa-solid fa-clock-rotate-left text-warning text-xl mt-1"
          ></i>
          <div class="flex-grow">
            <h3 class="font-extrabold text-amber-900 text-base leading-tight">
              Pending Admin Approval
            </h3>
            <p class="text-amber-800 text-sm mt-1 leading-relaxed">
              Your recent tutor profile changes were submitted on
              <strong>{{
                user.pendingTutorProfileUpdates.submittedAt | date: 'medium'
              }}</strong>
              and are currently awaiting administrator review. You can still
              make adjustments below; saving again will overwrite your previous
              pending request.
            </p>
          </div>
        </div>
        <button
          type="button"
          (click)="refreshProfileStatus()"
          [disabled]="isRefreshing"
          class="daisy-btn daisy-btn-outline daisy-btn-warning daisy-btn-sm w-full md:w-auto flex-shrink-0 gap-1.5 self-end md:self-auto"
        >
          <i
            class="fa-solid fa-arrows-rotate"
            [class.fa-spin]="isRefreshing"
          ></i>
          Refresh Status
        </button>
      </div>
    }

    <!-- Progress Stepper -->
    <div class="w-full overflow-x-auto pb-4">
      <ul class="daisy-steps w-full min-w-[600px]">
        <li class="daisy-step" [class.daisy-step-primary]="activeStep >= 1">
          Branding
        </li>
        <li class="daisy-step" [class.daisy-step-primary]="activeStep >= 2">
          Tuition
        </li>
        <li class="daisy-step" [class.daisy-step-primary]="activeStep >= 3">
          Location
        </li>
        <li class="daisy-step" [class.daisy-step-primary]="activeStep >= 4">
          Classes
        </li>
        <li class="daisy-step" [class.daisy-step-primary]="activeStep >= 5">
          Pricing
        </li>
        <li class="daisy-step" [class.daisy-step-primary]="activeStep >= 6">
          Verification
        </li>
      </ul>
    </div>

    <!-- Main Form Card -->
    <div
      class="daisy-card daisy-bg-base-100 shadow-xl border daisy-border-base-300"
    >
      <div class="daisy-card-body p-6 md:p-10">
        <!-- STEP 1: Branding -->
        <div *ngIf="activeStep === 1" class="space-y-8">
          <div>
            <h2 class="daisy-card-title text-2xl font-bold mb-2">
              Craft Your Digital Identity
            </h2>
            <p class="daisy-text-base-content/70">
              A teacher's brand is the bridge between silent knowledge and vocal
              influence. Ensure your presentation reflects the depth of your
              expertise.
            </p>
          </div>

          <!-- Profile Header Container -->
          <div
            class="w-full rounded-2xl daisy-bg-base-100 border daisy-border-base-300 shadow-sm mb-8 overflow-hidden"
          >
            <!-- Banner Section -->
            <div
              class="w-full relative daisy-bg-base-200"
              style="min-height: 200px"
            >
              <file-pond
                class="banner-filepond"
                [options]="bannerPondOptions"
                [files]="bannerPondFiles"
                (onprocessfile)="onBannerUploadSuccess($event)"
              ></file-pond>
            </div>

            <!-- Profile Info Section below Banner -->
            <div class="relative px-6 sm:px-8 pb-6 pt-4">
              <!-- Avatar Section overlapping the banner -->
              <div class="absolute -top-16 left-6 sm:left-8 z-10">
                <div
                  class="w-[140px] h-[140px] rounded-full shadow-md overflow-hidden"
                >
                  <black-user-avatar
                    [allowEdit]="true"
                    (uploadSuccess)="onAvatarUploadSuccess($event)"
                  ></black-user-avatar>
                </div>
              </div>

              <!-- Text Info next to Avatar -->
              <div class="ml-[140px] sm:ml-[160px]">
                <h3 class="text-xl sm:text-2xl font-bold">Profile Branding</h3>
                <p class="text-sm daisy-text-base-content/70 mt-1">
                  Upload a high-quality cover photo and profile picture to make
                  your profile stand out.
                </p>
              </div>
            </div>
          </div>

          <div class="daisy-divider">Profile URL</div>
          <div class="daisy-form-control w-full">
            <label class="daisy-label">
              <span class="daisy-label-text font-bold"
                >Profile Slug / Custom Handle
                <span class="text-error font-bold ml-1">*</span></span
              >
            </label>
            <div class="daisy-join w-full">
              <span
                class="daisy-join-item border border-r-0 daisy-border-base-300 daisy-bg-base-200 px-4 flex items-center text-sm text-base-content/50 select-none font-medium focus:ring-0 focus:border-0"
              >
                learnbird.lk/tutors/
              </span>
              <input
                type="text"
                formControlName="slug"
                placeholder="e.g. john-doe"
                class="daisy-input daisy-input-bordered daisy-join-item flex-grow"
                [class.daisy-input-error]="slugError"
                [class.daisy-input-success]="slugUnique && !slugChecking"
              />
            </div>
            <label class="daisy-label">
              <span
                *ngIf="slugChecking"
                class="daisy-label-text-alt text-base-content/60 flex items-center gap-1.5"
              >
                <span
                  class="daisy-loading daisy-loading-spinner daisy-loading-xs"
                ></span>
                Checking availability...
              </span>
              <span
                *ngIf="slugError && !slugChecking"
                class="daisy-label-text-alt text-error font-semibold"
              >
                {{ slugError }}
                <span *ngIf="slugSuggestion">
                  Try:
                  <button
                    type="button"
                    class="daisy-link daisy-link-primary font-bold"
                    (click)="useSuggestion(slugSuggestion)"
                  >
                    {{ slugSuggestion }}
                  </button>
                </span>
              </span>
              <span
                *ngIf="
                  slugUnique && !slugChecking && tutorForm.get('slug')?.value
                "
                class="daisy-label-text-alt text-success font-semibold flex items-center gap-1"
              >
                <i class="fa-solid fa-circle-check"></i> This profile URL is
                available!
              </span>
            </label>
          </div>

          <div class="daisy-divider">Basic Details</div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <label class="daisy-form-control w-full">
              <div class="daisy-label">
                <span class="daisy-label-text font-bold"
                  >Professional Headline</span
                >
              </div>
              <input
                type="text"
                formControlName="jobTitle"
                placeholder="e.g. Physics Expert"
                class="daisy-input daisy-input-bordered w-full"
              />
            </label>
            <label class="daisy-form-control w-full">
              <div class="daisy-label">
                <span class="daisy-label-text font-bold"
                  >Years of Experience</span
                >
              </div>
              <input
                type="text"
                formControlName="experience"
                placeholder="e.g. 15+ years"
                class="daisy-input daisy-input-bordered w-full"
              />
            </label>
          </div>

          <div class="daisy-form-control w-full">
            <div class="daisy-label">
              <span class="daisy-label-text font-bold"
                >Instructor Biography
                <span class="text-error font-bold ml-1">*</span></span
              >
            </div>
            <black-rich-text-editor
              formControlName="biography"
              placeholder="Tell students about your teaching experience, subjects, and style..."
              theme="snow"
              minHeight="220px"
            ></black-rich-text-editor>
            <div
              class="daisy-label"
              *ngIf="
                tutorForm.get('biography')?.touched &&
                tutorForm.get('biography')?.invalid
              "
            >
              <span
                *ngIf="tutorForm.get('biography')?.hasError('required')"
                class="daisy-label-text-alt text-error font-semibold"
              >
                Instructor biography is required.
              </span>
              <span
                *ngIf="tutorForm.get('biography')?.hasError('minlength')"
                class="daisy-label-text-alt text-error font-semibold"
              >
                Biography must be at least 20 characters long.
              </span>
            </div>
          </div>

          <div class="daisy-divider">Qualifications & Awards</div>

          <!-- Educational Qualifications -->
          <div class="space-y-4">
            <div class="flex justify-between items-center">
              <h3 class="font-bold">Educational Qualifications</h3>
              <button
                type="button"
                class="daisy-btn daisy-btn-sm daisy-btn-outline daisy-btn-primary"
                (click)="addQualification()"
              >
                <i class="fa-solid fa-plus text-[18px]"></i> Add Degree
              </button>
            </div>
            <div formArrayName="educationalQualifications" class="space-y-3">
              <div
                *ngFor="
                  let qual of educationalQualifications.controls;
                  let i = index
                "
                class="flex items-center gap-3"
              >
                <input
                  [formControlName]="i"
                  type="text"
                  placeholder="e.g. BSc (Hons) in Physics - University of Colombo"
                  class="daisy-input daisy-input-bordered w-full"
                />
                <button
                  type="button"
                  class="daisy-btn daisy-btn-square daisy-btn-error daisy-btn-outline"
                  (click)="removeQualification(i)"
                >
                  <i class="fa-solid fa-trash"></i>
                </button>
              </div>
              <div
                *ngIf="educationalQualifications.length === 0"
                class="text-sm daisy-text-base-content/50 italic"
              >
                No qualifications added yet.
              </div>
            </div>
          </div>

          <!-- Certifications -->
          <div class="space-y-4">
            <div class="flex justify-between items-center">
              <h3 class="font-bold">Certifications & Awards</h3>
              <button
                type="button"
                class="daisy-btn daisy-btn-sm daisy-btn-outline daisy-btn-primary"
                (click)="addCertification()"
              >
                <i class="fa-solid fa-plus text-[18px]"></i> Add Award
              </button>
            </div>
            <div formArrayName="certifications" class="space-y-3">
              <div
                *ngFor="let cert of certifications.controls; let i = index"
                class="flex items-center gap-3"
              >
                <input
                  [formControlName]="i"
                  type="text"
                  placeholder="e.g. Certified Educator - Cambridge Assessment International Education"
                  class="daisy-input daisy-input-bordered w-full"
                />
                <button
                  type="button"
                  class="daisy-btn daisy-btn-square daisy-btn-error daisy-btn-outline"
                  (click)="removeCertification(i)"
                >
                  <i class="fa-solid fa-trash"></i>
                </button>
              </div>
              <div
                *ngIf="certifications.length === 0"
                class="text-sm daisy-text-base-content/50 italic"
              >
                No certifications added yet.
              </div>
            </div>
          </div>
        </div>

        <!-- STEP 2: Setup (Subjects, Grades, Mediums, Formats) (Was Step 3) -->
        <div *ngIf="activeStep === 2" class="space-y-8">
          <div>
            <h2 class="daisy-card-title text-2xl font-bold mb-2">
              Subjects & Formats
            </h2>
            <p class="daisy-text-base-content/70">
              Select the subjects, grades, instruction mediums, and tuition
              delivery formats you support.
            </p>
          </div>

          <!-- Custom string Subjects instead of checkboxes -->
          <div class="space-y-4">
            <div class="flex justify-between items-center">
              <h3 class="font-bold">
                Subjects You Teach
                <span class="text-error font-bold ml-1">*</span>
              </h3>
              <button
                type="button"
                class="daisy-btn daisy-btn-sm daisy-btn-outline daisy-btn-primary"
                (click)="addSubject()"
              >
                <i class="fa-solid fa-plus text-[18px]"></i> Add Subject
              </button>
            </div>
            <div formArrayName="subjects" class="space-y-3">
              <div
                *ngFor="let sub of subjects.controls; let i = index"
                class="flex items-center gap-3"
              >
                <input
                  [formControlName]="i"
                  type="text"
                  placeholder="e.g. Advanced Level Physics"
                  class="daisy-input daisy-input-bordered w-full"
                  [class.daisy-input-error]="
                    step2Attempted && (!sub.value || !sub.value.trim())
                  "
                />
                <button
                  type="button"
                  class="daisy-btn daisy-btn-square daisy-btn-error daisy-btn-outline"
                  (click)="removeSubject(i)"
                >
                  <i class="fa-solid fa-trash"></i>
                </button>
              </div>
              <div
                *ngIf="subjects.length === 0"
                class="text-sm daisy-text-base-content/50 italic"
              >
                No subjects added yet.
              </div>
              <div
                *ngIf="step2Attempted && getValidSubjectsCount() === 0"
                class="text-sm text-error font-semibold mt-2 flex items-center gap-1.5"
              >
                <i class="fa-solid fa-circle-exclamation"></i> At least one
                valid subject is required.
              </div>
            </div>
          </div>

          <div class="space-y-3 mt-8">
            <label class="daisy-label"
              ><span class="daisy-label-text font-bold"
                >Grades & Curriculum Streams</span
              ></label
            >
            <div class="grid grid-cols-2 md:grid-cols-3 gap-3">
              <label
                *ngFor="let grd of gradesList"
                class="cursor-pointer daisy-label justify-start gap-3 p-3 rounded-lg border daisy-border-base-300 hover:daisy-bg-base-200 transition-colors"
                [class.daisy-bg-primary]="isChecked('grades', grd)"
                [class.bg-opacity-10]="isChecked('grades', grd)"
                [class.daisy-border-primary]="isChecked('grades', grd)"
              >
                <input
                  type="checkbox"
                  [checked]="isChecked('grades', grd)"
                  (change)="onCheckboxChange('grades', grd, $event)"
                  class="daisy-checkbox daisy-checkbox-primary daisy-checkbox-sm"
                />
                <span
                  class="daisy-label-text font-medium"
                  [class.daisy-text-primary]="isChecked('grades', grd)"
                  >{{ grd }}</span
                >
              </label>
            </div>
          </div>

          <div class="daisy-divider"></div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div class="space-y-3">
              <label class="daisy-label"
                ><span class="daisy-label-text font-bold"
                  >Instruction Mediums</span
                ></label
              >
              <div class="flex flex-col gap-3">
                <label
                  *ngFor="let med of mediumsList"
                  class="cursor-pointer daisy-label justify-start gap-3 p-3 rounded-lg border daisy-border-base-300 hover:daisy-bg-base-200 transition-colors"
                  [class.daisy-bg-primary]="isChecked('mediums', med)"
                  [class.bg-opacity-10]="isChecked('mediums', med)"
                  [class.daisy-border-primary]="isChecked('mediums', med)"
                >
                  <input
                    type="checkbox"
                    [checked]="isChecked('mediums', med)"
                    (change)="onCheckboxChange('mediums', med, $event)"
                    class="daisy-checkbox daisy-checkbox-primary daisy-checkbox-sm"
                  />
                  <span
                    class="daisy-label-text font-medium"
                    [class.daisy-text-primary]="isChecked('mediums', med)"
                    >{{ med }} Medium</span
                  >
                </label>
              </div>
            </div>

            <div class="space-y-3">
              <label class="daisy-label"
                ><span class="daisy-label-text font-bold"
                  >Delivery Formats</span
                ></label
              >
              <div class="flex flex-col gap-3">
                <label
                  class="cursor-pointer daisy-label justify-start gap-3 p-3 rounded-lg border daisy-border-base-300 hover:daisy-bg-base-200 transition-colors"
                  [class.daisy-bg-primary]="isChecked('classFormats', 'Online')"
                  [class.bg-opacity-10]="isChecked('classFormats', 'Online')"
                  [class.daisy-border-primary]="
                    isChecked('classFormats', 'Online')
                  "
                >
                  <input
                    type="checkbox"
                    [checked]="isChecked('classFormats', 'Online')"
                    (change)="
                      onCheckboxChange('classFormats', 'Online', $event)
                    "
                    class="daisy-checkbox daisy-checkbox-primary daisy-checkbox-sm"
                  />
                  <span
                    class="daisy-label-text font-medium"
                    [class.daisy-text-primary]="
                      isChecked('classFormats', 'Online')
                    "
                    >Online Class</span
                  >
                </label>
                <label
                  class="cursor-pointer daisy-label justify-start gap-3 p-3 rounded-lg border daisy-border-base-300 hover:daisy-bg-base-200 transition-colors"
                  [class.daisy-bg-primary]="
                    isChecked('classFormats', 'HomeVisit')
                  "
                  [class.bg-opacity-10]="isChecked('classFormats', 'HomeVisit')"
                  [class.daisy-border-primary]="
                    isChecked('classFormats', 'HomeVisit')
                  "
                >
                  <input
                    type="checkbox"
                    [checked]="isChecked('classFormats', 'HomeVisit')"
                    (change)="
                      onCheckboxChange('classFormats', 'HomeVisit', $event)
                    "
                    class="daisy-checkbox daisy-checkbox-primary daisy-checkbox-sm"
                  />
                  <span
                    class="daisy-label-text font-medium"
                    [class.daisy-text-primary]="
                      isChecked('classFormats', 'HomeVisit')
                    "
                    >Individual (Home Visit)</span
                  >
                </label>
                <label
                  class="cursor-pointer daisy-label justify-start gap-3 p-3 rounded-lg border daisy-border-base-300 hover:daisy-bg-base-200 transition-colors"
                  [class.daisy-bg-primary]="isChecked('classFormats', 'Group')"
                  [class.bg-opacity-10]="isChecked('classFormats', 'Group')"
                  [class.daisy-border-primary]="
                    isChecked('classFormats', 'Group')
                  "
                >
                  <input
                    type="checkbox"
                    [checked]="isChecked('classFormats', 'Group')"
                    (change)="onCheckboxChange('classFormats', 'Group', $event)"
                    class="daisy-checkbox daisy-checkbox-primary daisy-checkbox-sm"
                  />
                  <span
                    class="daisy-label-text font-medium"
                    [class.daisy-text-primary]="
                      isChecked('classFormats', 'Group')
                    "
                    >Group Class</span
                  >
                </label>
                <label
                  class="cursor-pointer daisy-label justify-start gap-3 p-3 rounded-lg border daisy-border-base-300 hover:daisy-bg-base-200 transition-colors"
                  [class.daisy-bg-primary]="
                    isChecked('classFormats', 'Institute')
                  "
                  [class.bg-opacity-10]="isChecked('classFormats', 'Institute')"
                  [class.daisy-border-primary]="
                    isChecked('classFormats', 'Institute')
                  "
                >
                  <input
                    type="checkbox"
                    [checked]="isChecked('classFormats', 'Institute')"
                    (change)="
                      onCheckboxChange('classFormats', 'Institute', $event)
                    "
                    class="daisy-checkbox daisy-checkbox-primary daisy-checkbox-sm"
                  />
                  <span
                    class="daisy-label-text font-medium"
                    [class.daisy-text-primary]="
                      isChecked('classFormats', 'Institute')
                    "
                    >Institute / Hall Class</span
                  >
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- STEP 3: Locations Coverage (Was Step 4) -->
        <div *ngIf="activeStep === 3" class="space-y-8">
          <div>
            <h2 class="daisy-card-title text-2xl font-bold mb-2">
              Location Coverage
            </h2>
            <p class="daisy-text-base-content/70">
              If you conduct offline classes (home visits or group classes),
              select the Sri Lankan districts you can travel to.
            </p>
          </div>

          <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
            <label
              *ngFor="let dist of districtsList"
              class="cursor-pointer daisy-label justify-start gap-3 p-3 rounded-xl border daisy-border-base-300 hover:daisy-bg-base-200 transition-colors"
              [class.daisy-bg-primary]="isChecked('districts', dist)"
              [class.bg-opacity-10]="isChecked('districts', dist)"
              [class.daisy-border-primary]="isChecked('districts', dist)"
            >
              <input
                type="checkbox"
                [checked]="isChecked('districts', dist)"
                (change)="onCheckboxChange('districts', dist, $event)"
                class="daisy-checkbox daisy-checkbox-primary daisy-checkbox-sm"
              />
              <span
                class="daisy-label-text font-medium"
                [class.daisy-text-primary]="isChecked('districts', dist)"
                >{{ dist }}</span
              >
            </label>
          </div>
        </div>

        <!-- STEP 4: Physical Classes (Was Step 5) -->
        <div *ngIf="activeStep === 4" class="space-y-8">
          <div
            class="flex flex-col md:flex-row justify-between items-start md:items-center gap-4"
          >
            <div>
              <h2 class="daisy-card-title text-2xl font-bold mb-2">
                Physical Classes
              </h2>
              <p class="daisy-text-base-content/70">
                Manage physical/offline classes you conduct. Students will
                coordinate enrollment via WhatsApp.
              </p>
            </div>
            <button
              type="button"
              (click)="addExternalClass()"
              class="daisy-btn daisy-btn-primary"
            >
              <i class="fa-solid fa-plus text-[20px]"></i> Add Class
            </button>
          </div>

          <div formArrayName="externalClasses" class="space-y-6">
            <div
              *ngFor="let cls of externalClasses.controls; let i = index"
              [formGroupName]="i"
              class="daisy-card daisy-bg-base-200 border daisy-border-base-300"
            >
              <div class="daisy-card-body p-6">
                <div class="flex justify-between items-start mb-4">
                  <div class="flex items-center gap-3">
                    <div class="daisy-avatar daisy-placeholder">
                      <div
                        class="daisy-bg-neutral daisy-text-neutral-content rounded-full w-10"
                      >
                        <i class="fa-solid fa-graduation-cap"></i>
                      </div>
                    </div>
                    <h3 class="font-bold text-lg">
                      Class #{{ i + 1 }} Details
                    </h3>
                  </div>
                  <button
                    type="button"
                    (click)="removeExternalClass(i)"
                    class="daisy-btn daisy-btn-square daisy-btn-error daisy-btn-sm daisy-btn-outline"
                  >
                    <i class="fa-solid fa-trash"></i>
                  </button>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label class="daisy-form-control w-full">
                    <div class="daisy-label">
                      <span class="daisy-label-text font-bold"
                        >Class Title</span
                      >
                    </div>
                    <input
                      formControlName="title"
                      type="text"
                      placeholder="e.g. 2027 A/L Physics Theory"
                      class="daisy-input daisy-input-bordered w-full"
                    />
                  </label>
                  <label class="daisy-form-control w-full">
                    <div class="daisy-label">
                      <span class="daisy-label-text font-bold">Subject</span>
                    </div>
                    <input
                      formControlName="subject"
                      type="text"
                      placeholder="e.g. Physics"
                      class="daisy-input daisy-input-bordered w-full"
                    />
                  </label>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <label class="daisy-form-control w-full">
                    <div class="daisy-label">
                      <span class="daisy-label-text font-bold">Medium</span>
                    </div>
                    <select
                      formControlName="medium"
                      class="daisy-select daisy-select-bordered w-full"
                    >
                      <option value="ENGLISH">English</option>
                      <option value="SINHALA">Sinhala</option>
                      <option value="TAMIL">Tamil</option>
                    </select>
                  </label>
                  <label class="daisy-form-control w-full">
                    <div class="daisy-label">
                      <span class="daisy-label-text font-bold"
                        >Grade Level</span
                      >
                    </div>
                    <select
                      formControlName="grade"
                      class="daisy-select daisy-select-bordered w-full"
                    >
                      <option value="">Select Grade</option>
                      <option *ngFor="let g of gradesList" [value]="g">
                        {{ g }}
                      </option>
                    </select>
                  </label>
                  <label class="daisy-form-control w-full">
                    <div class="daisy-label">
                      <span class="daisy-label-text font-bold">Format</span>
                    </div>
                    <select
                      formControlName="classType"
                      class="daisy-select daisy-select-bordered w-full"
                    >
                      <option value="Individual">Individual (Home)</option>
                      <option value="Group">Group</option>
                      <option value="Institute">Institute</option>
                    </select>
                  </label>
                </div>

                <div
                  formGroupName="location"
                  class="grid grid-cols-1 md:grid-cols-3 gap-4 daisy-bg-base-100 p-4 rounded-xl mt-2 border daisy-border-base-300"
                >
                  <label class="daisy-form-control w-full">
                    <div class="daisy-label">
                      <span class="daisy-label-text font-bold">District</span>
                    </div>
                    <select
                      formControlName="district"
                      (change)="onPhysicalClassDistrictChange(i, $event)"
                      class="daisy-select daisy-select-bordered w-full"
                    >
                      <option value="">Select District</option>
                      <option *ngFor="let d of districtsList" [value]="d">
                        {{ d }}
                      </option>
                    </select>
                  </label>
                  <label class="daisy-form-control w-full">
                    <div class="daisy-label">
                      <span class="daisy-label-text font-bold">City</span>
                    </div>
                    <select
                      formControlName="city"
                      class="daisy-select daisy-select-bordered w-full"
                    >
                      <option value="">Select City</option>
                      <option
                        *ngFor="let c of citiesForSelectedPhysicalClass[i]"
                        [value]="c"
                      >
                        {{ c }}
                      </option>
                    </select>
                  </label>
                  <label class="daisy-form-control w-full">
                    <div class="daisy-label">
                      <span class="daisy-label-text font-bold"
                        >Venue / Building</span
                      >
                    </div>
                    <input
                      formControlName="venue"
                      type="text"
                      placeholder="e.g. Rotary Hall"
                      class="daisy-input daisy-input-bordered w-full"
                    />
                  </label>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                  <label class="daisy-form-control w-full">
                    <div class="daisy-label">
                      <span class="daisy-label-text font-bold"
                        >Class Schedule</span
                      >
                    </div>
                    <input
                      formControlName="schedule"
                      type="text"
                      placeholder="e.g. Saturdays 8 AM - 12 PM"
                      class="daisy-input daisy-input-bordered w-full"
                    />
                  </label>
                  <label class="daisy-form-control w-full">
                    <div class="daisy-label">
                      <span class="daisy-label-text font-bold"
                        >Fee Structure</span
                      >
                    </div>
                    <input
                      formControlName="feeDetails"
                      type="text"
                      placeholder="e.g. LKR 3,000 / month"
                      class="daisy-input daisy-input-bordered w-full"
                    />
                  </label>
                </div>
              </div>
            </div>

            <div
              *ngIf="externalClasses.length === 0"
              class="flex flex-col items-center justify-center p-12 daisy-bg-base-200 rounded-2xl border-2 border-dashed daisy-border-base-300 text-center"
            >
              <i
                class="fa-solid fa-graduation-cap daisy-text-base-content/30 text-5xl mb-4"
              ></i>
              <h3 class="font-bold text-lg mb-2">No Physical Classes Added</h3>
              <p
                class="daisy-text-base-content/70 text-sm mb-6 max-w-sm mx-auto"
              >
                You haven't listed any offline or physical location classes yet.
                Click below to add one.
              </p>
              <button
                type="button"
                (click)="addExternalClass()"
                class="daisy-btn daisy-btn-outline"
              >
                Add Your First Class
              </button>
            </div>
          </div>
        </div>

        <!-- STEP 5: Pricing & Contact (Was Step 6) -->
        <div *ngIf="activeStep === 5" class="space-y-8">
          <div>
            <h2 class="daisy-card-title text-2xl font-bold mb-2">
              Pricing & Contact Details
            </h2>
            <p class="daisy-text-base-content/70">
              Configure your general tutoring fee rates and direct contact
              channels for student inquiries.
            </p>
          </div>

          <!-- Public Status Toggle -->
          <div
            class="daisy-card bg-base-200 border border-base-300 rounded-2xl p-5 shadow-sm"
          >
            <div class="flex items-center justify-between gap-4">
              <div class="flex-grow">
                <h3 class="font-bold text-base flex items-center gap-2">
                  <i class="fa-solid fa-globe text-primary"></i>
                  Public Profile Visibility
                </h3>
                <p class="text-xs text-base-content/60 mt-1 max-w-xl">
                  When enabled, your profile will be listed in the public tutor
                  directory and visible to search engines. If disabled, only
                  students who have direct links can view your profile, and you
                  won't appear in search listings.
                </p>
              </div>
              <label class="daisy-form-control flex-shrink-0">
                <input
                  type="checkbox"
                  formControlName="isPublic"
                  class="daisy-toggle daisy-toggle-primary daisy-toggle-lg"
                />
              </label>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <label class="daisy-form-control w-full">
              <div class="daisy-label">
                <span class="daisy-label-text font-bold"
                  >General Hourly Rate (LKR)</span
                >
              </div>
              <input
                formControlName="ratePerHour"
                type="number"
                placeholder="e.g. 1500"
                class="daisy-input daisy-input-bordered w-full"
              />
            </label>
            <label class="daisy-form-control w-full">
              <div class="daisy-label">
                <span class="daisy-label-text font-bold"
                  >General Monthly Rate (LKR)</span
                >
              </div>
              <input
                formControlName="ratePerMonth"
                type="number"
                placeholder="e.g. 3500"
                class="daisy-input daisy-input-bordered w-full"
              />
            </label>
          </div>

          <label class="daisy-form-control w-full">
            <div class="daisy-label">
              <span class="daisy-label-text font-bold"
                >Pricing Details / Special Notes</span
              >
            </div>
            <textarea
              formControlName="pricingDetails"
              rows="3"
              placeholder="e.g. LKR 1,500/hr for individual classes, discounts apply for group bookings."
              class="daisy-textarea daisy-textarea-bordered w-full"
            ></textarea>
          </label>

          <div class="daisy-divider">Contact Info & Socials</div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <label class="daisy-form-control w-full">
              <div class="daisy-label">
                <span class="daisy-label-text font-bold"
                  >WhatsApp Contact Number
                  <span class="text-error font-bold ml-1">*</span></span
                >
              </div>
              <label
                class="daisy-input daisy-input-bordered flex items-center gap-2"
                [class.daisy-input-error]="
                  tutorForm.get('whatsAppNumber')?.touched &&
                  tutorForm.get('whatsAppNumber')?.invalid
                "
              >
                <i
                  class="fa-brands fa-whatsapp daisy-text-base-content/50 text-sm"
                ></i>
                <input
                  formControlName="whatsAppNumber"
                  type="text"
                  class="grow"
                  placeholder="e.g. +94771234567"
                />
              </label>
              <div
                class="daisy-label"
                *ngIf="
                  tutorForm.get('whatsAppNumber')?.touched &&
                  tutorForm.get('whatsAppNumber')?.invalid
                "
              >
                <span
                  *ngIf="tutorForm.get('whatsAppNumber')?.hasError('required')"
                  class="daisy-label-text-alt text-error font-semibold"
                >
                  WhatsApp contact number is required.
                </span>
                <span
                  *ngIf="tutorForm.get('whatsAppNumber')?.hasError('pattern')"
                  class="daisy-label-text-alt text-error font-semibold"
                >
                  Invalid phone number format.
                </span>
              </div>
            </label>
            <label class="daisy-form-control w-full">
              <div class="daisy-label">
                <span class="daisy-label-text font-bold"
                  >YouTube Promo Channel</span
                >
              </div>
              <label
                class="daisy-input daisy-input-bordered flex items-center gap-2"
              >
                <i
                  class="fa-brands fa-youtube daisy-text-base-content/50 text-sm"
                ></i>
                <input
                  formControlName="youtubeChannel"
                  type="text"
                  class="grow"
                  placeholder="https://youtube.com/..."
                />
              </label>
            </label>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <label class="daisy-form-control w-full">
              <div class="daisy-label">
                <span class="daisy-label-text font-bold">Facebook</span>
              </div>
              <input
                formControlName="facebook"
                type="text"
                placeholder="https://facebook.com/..."
                class="daisy-input daisy-input-bordered w-full"
              />
            </label>
            <label class="daisy-form-control w-full">
              <div class="daisy-label">
                <span class="daisy-label-text font-bold">LinkedIn</span>
              </div>
              <input
                formControlName="linkedin"
                type="text"
                placeholder="https://linkedin.com/in/..."
                class="daisy-input daisy-input-bordered w-full"
              />
            </label>
            <label class="daisy-form-control w-full">
              <div class="daisy-label">
                <span class="daisy-label-text font-bold">Website</span>
              </div>
              <input
                formControlName="website"
                type="text"
                placeholder="https://..."
                class="daisy-input daisy-input-bordered w-full"
              />
            </label>
          </div>
        </div>

        <!-- STEP 6: Verification (Was Step 2) -->
        <div *ngIf="activeStep === 6" class="space-y-8">
          <div>
            <h2 class="daisy-card-title text-2xl font-bold mb-2">
              Tutor Vetting Documents
              <span
                class="text-xs font-normal text-base-content/60 bg-base-200 px-2.5 py-1 rounded-full ml-2"
                >Optional Step</span
              >
            </h2>
            <p class="daisy-text-base-content/70">
              Upload your documents to verify your identity and expertise. This
              step is <strong>optional</strong>—you can save your profile now
              and submit verification documents later to earn the "Verified
              Tutor" badge.
            </p>
          </div>

          <div class="daisy-alert daisy-alert-info shadow-sm">
            <i class="fa-solid fa-shield-halved"></i>
            <div>
              <h3 class="font-bold">Privacy & Verification Note</h3>
              <div class="text-sm">
                Verification documents are kept strictly confidential, stored
                securely, and never shared publicly. Uploading documents unlocks
                the "Verified Tutor" badge for your profile.
              </div>
            </div>
          </div>

          <div class="space-y-6">
            <div class="daisy-form-control">
              <label class="daisy-label"
                ><span class="daisy-label-text font-bold"
                  >National Identity Card (NIC) Front / Passport Bio Page
                  <span class="text-xs text-base-content/50 font-normal ml-1"
                    >(Optional)</span
                  ></span
                ></label
              >
              <div
                class="p-4 daisy-bg-base-200/50 rounded-xl border-2 border-dashed daisy-border-base-300"
              >
                <file-pond
                  [options]="pondOptions"
                  (pondProcessFile)="onNicFrontUploadSuccess($event)"
                ></file-pond>
                <div
                  class="text-success text-sm font-medium mt-2"
                  *ngIf="tutorForm.value.nicFront"
                >
                  ✓ File uploaded successfully
                </div>
              </div>
            </div>

            <div class="daisy-form-control">
              <label class="daisy-label"
                ><span class="daisy-label-text font-bold"
                  >National Identity Card (NIC) Back Page
                  <span class="text-xs text-base-content/50 font-normal ml-1"
                    >(Optional)</span
                  ></span
                ></label
              >
              <div
                class="p-4 daisy-bg-base-200/50 rounded-xl border-2 border-dashed daisy-border-base-300"
              >
                <file-pond
                  [options]="pondOptions"
                  (pondProcessFile)="onNicBackUploadSuccess($event)"
                ></file-pond>
                <div
                  class="text-success text-sm font-medium mt-2"
                  *ngIf="tutorForm.value.nicBack"
                >
                  ✓ File uploaded successfully
                </div>
              </div>
            </div>

            <div class="daisy-form-control">
              <label class="daisy-label"
                ><span class="daisy-label-text font-bold"
                  >Highest Educational Degree Certificate
                  <span class="text-xs text-base-content/50 font-normal ml-1"
                    >(Optional)</span
                  ></span
                ></label
              >
              <div
                class="p-4 daisy-bg-base-200/50 rounded-xl border-2 border-dashed daisy-border-base-300"
              >
                <file-pond
                  [options]="pondOptions"
                  (pondProcessFile)="onAcademicCertUploadSuccess($event)"
                ></file-pond>
                <div
                  class="text-success text-sm font-medium mt-2"
                  *ngIf="tutorForm.value.academicCertificate"
                >
                  ✓ File uploaded successfully
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Form Navigation -->
        <div
          class="daisy-card-actions justify-between mt-10 pt-6 border-t daisy-border-base-300"
        >
          <button
            type="button"
            (click)="prevStep()"
            [disabled]="activeStep === 1"
            class="daisy-btn daisy-btn-ghost"
          >
            <i class="fa-solid fa-arrow-left"></i> Previous
          </button>

          <button
            *ngIf="activeStep < totalSteps"
            type="button"
            (click)="nextStep()"
            class="daisy-btn daisy-btn-primary px-8"
          >
            Next Step <i class="fa-solid fa-arrow-right"></i>
          </button>

          <div
            *ngIf="activeStep === totalSteps"
            class="flex items-center gap-3"
          >
            <button
              type="button"
              (click)="saveProfile()"
              class="daisy-btn daisy-btn-ghost text-base-content/70 hover:text-base-content"
              *ngIf="!tutorForm.value.nicFront && !tutorForm.value.nicBack"
            >
              Skip & Save Profile
            </button>

            <button
              type="button"
              (click)="saveProfile()"
              class="daisy-btn daisy-btn-success text-white px-8"
            >
              <i class="fa-solid fa-floppy-disk"></i> Save Profile
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</form>
